import { Stack } from '@mui/material'
import CatchmentAction from 'catchment/actions/CatchmentAction'
import { push } from '@lagunovsky/redux-react-router'
import React, { useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { getStationArrowNav } from 'utils/ActionUtils'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import useUpdateEffect from 'utils/customHook/useUpdateEffect'
import CatchmentForm from './CatchmentForm'
import CatchmentPointsTable from './CatchmentPointsTable'
import CatchmentSDAGETable from './CatchmentSDAGETable'
import { useParams } from 'react-router'
import sandre from 'assets/pictures/logos/sandre.png'
import mtes from 'assets/pictures/logos/mtes.png'
import useSandreList from 'utils/customHook/useSandreList'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import { compact } from 'lodash'

const CatchmentDescriptionApp = () => {
    const { id } = useParams()
    const {
        catchmentProps,
        catchments,
    } = useSelector(store => ({
        catchmentProps: store.CatchmentReducer.catchment,
        catchments: store.CatchmentReducer.catchments,
    }), shallowEqual)

    const [catchment, setCatchment] = useState(catchmentProps)

    const dispatch = useDispatch()

    const [readMode, setReadMode] = useState(id !== 'new')

    const agencies = useSandreList(SANDRE.AGENCES)

    useTitle(() => {
        if (id !== 'new') {
            return [{
                title: i18n.catchments,
                href: 'catchment',
            }, {
                title: catchmentProps.code ? `[${catchmentProps.code}] ${catchmentProps.name ?? ''}` : catchmentProps.name || i18n.unknown,
                href: `station/catchment/${catchmentProps.id}`,
            }, {
                title: i18n.dashboard,
                href: `station/catchment/${catchmentProps.id}`,
            }]
        }
        return [{
            title: i18n.catchments,
            href: 'catchment',
        }, {
            title: i18n.new,
            href: 'station/catchment/new',
        }]
    }, [catchmentProps])

    useUpdateEffect(() => {
        setCatchment(catchmentProps)
    }, [catchmentProps])

    useActions(() => {
        if (readMode) {
            const agency = agencies.find(a => a.code === catchmentProps.pond)
            const ref = agency?.mnemonique || agency?.reference
            const mtesLink = ref ? {
                img: mtes,
                href: `http://www.deb.developpement-durable.gouv.fr/telechargements/fiche.php?IG=${ref}_${catchmentProps.code}`,
                label: i18n.mtes,
            } : undefined
            const sandreLink = catchmentProps.sandreCode ? {
                img: sandre,
                href: `https://id.eaufrance.fr/AAC/${catchmentProps.sandreCode}`,
                label: i18n.sandre,
            } : undefined
            return {
                edit: () => setReadMode(false),
                arrowNav: getStationArrowNav('catchment', catchments, catchmentProps.id, s => dispatch(push(`/station/catchment/${s.id}/description`))),
                links: compact([mtesLink, sandreLink]),
                delete: () => dispatch(CatchmentAction.deleteCatchment(id)).then(result => result?.delete && dispatch(push('/catchment'))),
            }
        }
        return {
            cancel: () => {
                if (id !== 'new') {
                    setReadMode(true)
                    setCatchment(catchmentProps)
                } else {
                    dispatch(push('/catchment'))
                }
            },
            save: () => {
                if (id === 'new') {
                    dispatch(CatchmentAction.insertCatchment(catchment)).then(result => {
                        if (result?.insert) {
                            dispatch(push(`/station/catchment/${result.id}/description`))
                            setReadMode(true)
                        }
                    })
                } else {
                    dispatch(CatchmentAction.updateCatchment(id, catchment)).then(result => {
                        if (result?.update) {
                            dispatch(CatchmentAction.fetchCatchment(id))
                            setReadMode(true)
                        }
                    })
                }
            },
        }
    }, [readMode, catchment])

    return (
        <Stack container spacing={1} style={{ paddingBottom: '100' }}>
            <CatchmentForm catchment={catchment} setCatchment={setCatchment} readMode={readMode} />
            <CatchmentPointsTable catchmentPoints={catchment.catchmentPoints} setCatchment={setCatchment} readMode={readMode} />
            <CatchmentSDAGETable sdages={catchment.sdages} setCatchment={setCatchment} readMode={readMode} />
        </Stack>
    )
}

export default CatchmentDescriptionApp